import type { Building, BuildingId, Resources } from '@/models/types';
import type { Synergy } from '@/models/synergies';
import { IdleCalculator } from '@/systems/IdleCalculator';
import { INITIAL_RESOURCES, RESOURCE_TYPES } from '@/models/constants';
import { ResourceManager } from '@/systems/ResourceManager';
import { calculateSynergyBonus, SYNERGIES_ANCIENT } from '@/models/synergies';

export class BuildingManager {
  private levels: Record<BuildingId, number> = {};
  private readonly buildings: readonly Building[];
  private synergies: readonly Synergy[] = SYNERGIES_ANCIENT;
  private costReduction: number = 0;
  private synergyBoostMultiplier: number = 0;

  constructor(buildings: readonly Building[]) {
    this.buildings = buildings;
    for (const b of buildings) {
      this.levels[b.id] = 0;
    }
  }

  getLevel(id: BuildingId): number {
    return this.levels[id] ?? 0;
  }

  getLevels(): Record<BuildingId, number> {
    return { ...this.levels };
  }

  setLevels(levels: Record<BuildingId, number>): void {
    for (const b of this.buildings) {
      this.levels[b.id] = levels[b.id] ?? 0;
    }
  }

  getUpgradeCost(id: BuildingId): Resources {
    const building = this.buildings.find(b => b.id === id);
    if (!building) return { ...INITIAL_RESOURCES };
    const baseCost = IdleCalculator.calculateBuildingCost(building.baseCost, building.costMultiplier, this.getLevel(id));
    if (this.costReduction > 0) {
      const discount = Math.min(this.costReduction, 0.9); // cap at 90% reduction
      for (const rt of RESOURCE_TYPES) {
        baseCost[rt] = Math.floor(baseCost[rt] * (1 - discount));
      }
    }
    return baseCost;
  }

  upgrade(id: BuildingId, resources: ResourceManager): boolean {
    const cost = this.getUpgradeCost(id);
    if (!resources.canAfford(cost)) return false;
    resources.subtract(cost);
    this.levels[id] = this.getLevel(id) + 1;
    return true;
  }

  setSynergies(synergies: readonly Synergy[]): void {
    this.synergies = synergies;
  }

  setTalentBonuses(costReduction: number, synergyBoost: number): void {
    this.costReduction = costReduction;
    this.synergyBoostMultiplier = synergyBoost;
  }

  getSynergyBonus(buildingId: BuildingId): number {
    const base = calculateSynergyBonus(buildingId, this.levels, this.synergies);
    return base * (1 + this.synergyBoostMultiplier);
  }

  getProductionRates(): Resources {
    const rates = { ...INITIAL_RESOURCES };
    for (const b of this.buildings) {
      const level = this.getLevel(b.id);
      const synergyBonus = this.getSynergyBonus(b.id);
      const production = IdleCalculator.calculateBuildingProduction(b.baseProduction, level, synergyBonus);
      rates[b.resource] += production;
    }
    return rates;
  }

  getTotalProductionRate(): number {
    const rates = this.getProductionRates();
    return RESOURCE_TYPES.reduce((sum, rt) => sum + rates[rt], 0);
  }

  getBuildings(): readonly Building[] {
    return this.buildings;
  }
}
